import { createSlice } from '@reduxjs/toolkit';
import {
  createTrendingItemThunk,
  fetchTrendingItemsThunk,
  fetchTrendingItemByIdThunk,
  updateTrendingItemThunk,
  deleteTrendingItemThunk,
  incrementTrendingViewThunk,
  fetchTrendingPostsForStock,
  fetchTrendingBatchThunk,
} from './trendingActionThunks';

const initialState = {
  trendingItems: [],
  trendingBatch: [],
  selectedTrendingItem: null,
  loading: false,
  error: null,
  batchLoading: false,
  batchError: null,
};

const trendingSlice = createSlice({
  name: 'trending',
  initialState,
  reducers: {
    resetError: (state) => {
      state.error = null;
      state.batchError = null;
    },
    resetState: () => initialState,
    clearBatch: (state) => {
      state.trendingBatch = [];
      state.batchError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Trending Item
      .addCase(createTrendingItemThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTrendingItemThunk.fulfilled, (state, action) => {
        state.trendingItems = [...state.trendingItems, action.payload];
        state.loading = false;
      })
      .addCase(createTrendingItemThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch All Trending Items
      .addCase(fetchTrendingItemsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrendingItemsThunk.fulfilled, (state, action) => {
        state.trendingItems = action.payload;
        state.loading = false;
      })
      .addCase(fetchTrendingItemsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch One Trending Item
      .addCase(fetchTrendingItemByIdThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrendingItemByIdThunk.fulfilled, (state, action) => {
        state.selectedTrendingItem = action.payload;
        state.loading = false;
      })
      .addCase(fetchTrendingItemByIdThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Trending Item
      .addCase(updateTrendingItemThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTrendingItemThunk.fulfilled, (state, action) => {
        state.trendingItems = state.trendingItems.map(item =>
          item.id === action.payload.id ? action.payload : item
        );
        if (state.selectedTrendingItem?.id === action.payload.id) {
          state.selectedTrendingItem = action.payload;
        }
        state.loading = false;
      })
      .addCase(updateTrendingItemThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Trending Item
      .addCase(deleteTrendingItemThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTrendingItemThunk.fulfilled, (state, action) => {
        state.trendingItems = state.trendingItems.filter(item => item.id !== action.payload);
        if (state.selectedTrendingItem?.id === action.payload) {
          state.selectedTrendingItem = null;
        }
        state.loading = false;
      })
      .addCase(deleteTrendingItemThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Increment trending view count
      .addCase(incrementTrendingViewThunk.fulfilled, (state, action) => {
        const idx = state.trendingItems.findIndex(item => item.id === action.payload.post_id);
        if (idx !== -1) {
          state.trendingItems[idx].view_count = action.payload.count;
        }
        if (state.selectedTrendingItem?.id === action.payload.post_id) {
          state.selectedTrendingItem.view_count = action.payload.count;
        }
      })

      // Fetch trending posts for a single stock
      .addCase(fetchTrendingPostsForStock.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrendingPostsForStock.fulfilled, (state, action) => {
        state.trendingItems = action.payload;
        state.loading = false;
      })
      .addCase(fetchTrendingPostsForStock.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // BATCH: Fetch trending posts for multiple stocks
      .addCase(fetchTrendingBatchThunk.pending, (state) => {
        state.batchLoading = true;
        state.batchError = null;
      })
      .addCase(fetchTrendingBatchThunk.fulfilled, (state, action) => {
        state.trendingBatch = action.payload;
        state.batchLoading = false;
      })
      .addCase(fetchTrendingBatchThunk.rejected, (state, action) => {
        state.batchLoading = false;
        state.batchError = action.payload;
      });
  },
});

export const { resetError, resetState, clearBatch } = trendingSlice.actions;
export default trendingSlice.reducer;